// Small floating badge that surfaces pending offline-queued writes. Shown
// only when there are queued entries OR the browser reports offline so it
// stays out of the way during normal operation.
//
// Tapping the badge opens a detail modal listing every pending entry —
// kind, when it was queued, attempts so far, last error. Inside the modal
// the operator can hit "Sync now" to drain manually instead of waiting
// for the next interval. The previous "tap = silent flush" behaviour
// hid every queued entry behind a single number, which left operators
// guessing whether the tipping they just recorded had actually been
// captured.

import { useEffect, useState } from 'react'
import { CloudOff, RefreshCw, RotateCw, Check, AlertCircle, Clock, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import {
  drainQueue, clearDrainBackoff, onQueueChanged, pendingBreakdown, listPending, discardQueueEntry, retryQueueEntry,
  retryAllFailed,
  type QueueBreakdown, type Entry, type EntryKind,
} from '@/lib/offlineQueue'
import { confirmDialog } from '@/components/Dialogs'
import Modal from '@/components/Modal'
import { queueAgeing, queueAgeText, type QueueAgeing } from '@/lib/offlineQueueAgeing'

const AGEING_TICK_MS = 60_000
const OK_AGEING: QueueAgeing = { state: 'ok', oldestAgeMs: null }

// iOS Safari / iOS PWA can't drain the queue while the app is backgrounded
// (no Background Sync API), so a queued write only syncs while the app is open
// + foregrounded. Surface a nudge so operators don't lock the phone with work
// still pending. UA sniff is fine for a non-critical hint; iPadOS 13+ reports
// as Mac, so also treat a touch-capable "Mac" as iPad.
const IS_IOS = typeof navigator !== 'undefined' && (
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1)
)

const EMPTY: QueueBreakdown = { total: 0, tipping: 0, cooling_batch: 0, cooling_transition: 0, batch_tip: 0, pallet: 0, pallet_receipt: 0, scouting: 0, scout_walk: 0, farm_problem: 0, trap_visit: 0, security_incident: 0, pick: 0, pick_tallies: 0, packout_drops: 0, spray: 0, spray_complete: 0, reject: 0, phenology: 0, fert: 0, fert_complete: 0, irrigation: 0, drain_reading: 0, qc: 0, delivery: 0, fuel: 0, equipment_service: 0, labour_attest: 0, labour_adjust: 0, stock_movement: 0, stock_count_line: 0, span_clock: 0, harvest_load: 0, harvest_general: 0, harvest_team_move: 0, farm_day_lend: 0, piece_bag_issue: 0, ppe_issue: 0, farm_load: 0, vehicle_trip: 0, field_operation: 0, failed: 0 }

// Friendly labels for each queue-kind so the modal shows "Tipping" not
// "tipping". Centralised so a future EntryKind is one line to add here.
const KIND_LABEL: Record<EntryKind, string> = {
  tipping:       'Tipping',
  cooling_batch: 'Cooling batch',
  cooling_transition: 'Cooling move',
  batch_tip:     'Batch tip',
  pallet:        'Pallet',
  pallet_receipt: 'Pallet receipt',
  scouting:      'Scout observation',
  scout_walk:    'Scout walk',
  farm_problem:  'Farm problem',
  trap_visit:    'Trap visit',
  security_incident: 'Security incident',
  pick:          'Harvest pick',
  pick_tallies:  'Piece-work drops',
  packout_drops: 'Packing scans',
  spray:         'Spray record',
  spray_complete: 'Spray completion',
  reject:        'Reject log',
  phenology:     'Phenology event',
  fert:          'Fertiliser application',
  fert_complete: 'Fertiliser recorded as done',
  irrigation:    'Irrigation log',
  drain_reading: 'Drain reading',
  qc:            'QC check',
  delivery:      'Delivery',
  fuel:          'Fuel movement',
  equipment_service: 'Equipment service',
  labour_attest: 'Manager attestation',
  labour_adjust: 'Time adjustment',
  stock_movement: 'Stock movement',
  stock_count_line: 'Counted line',
  span_clock:    'Team clock-ins',
  harvest_load:  'Tractor loads',
  harvest_general: 'General work',
  harvest_team_move: 'Team move',
  farm_day_lend: 'Crew lend',
  piece_bag_issue: 'Bag issue',
  ppe_issue:     'PPE issue',
  // CPB — a wagon sent to a packhouse. Deliberately not 'Load': that word is
  // already taken by harvest_load (BigHarvest's tractor loads) right above.
  farm_load:     'Wagon to packhouse', security_patrol_scan: 'Patrol scan',
  vehicle_trip:  'Logbook trip',
  field_operation: 'Field operation',
}

// How long the oldest waiting save has sat on this phone. Re-reads the queue
// on every change, and ticks while anything waits so the age advances with no
// queue change (a phone sitting in a dead zone).
function useQueueAgeing(count: number): QueueAgeing {
  const [stamps, setStamps] = useState<ReadonlyArray<{ queued_at: string; failed?: boolean }>>([])
  const [nowMs,  setNowMs]  = useState(() => Date.now())

  useEffect(() => {
    const refresh = () => {
      void listPending().then(list => {
        setStamps(list.map(e => ({ queued_at: e.queued_at, failed: e.failed })))
        setNowMs(Date.now())
      })
    }
    refresh()
    return onQueueChanged(refresh)
  }, [])

  useEffect(() => {
    if (count === 0) return
    const id = setInterval(() => setNowMs(Date.now()), AGEING_TICK_MS)
    return () => clearInterval(id)
  }, [count])

  return count > 0 ? queueAgeing(stamps, nowMs) : OK_AGEING
}

// The badge's fill. A critical age beats every other state: the work on this
// phone matters more than whether it is online right now.
function badgeFill(ageing: QueueAgeing, online: boolean, count: number): string {
  if (ageing.state === 'critical') return 'bg-danger-fill text-on-fill'
  if (!online)   return 'bg-amber-700 hover:bg-amber-600 text-white'
  if (count > 0) return 'bg-blue-700 hover:bg-blue-600 text-white'
  return 'bg-emerald-700 text-white'
}

function badgeText(ageing: QueueAgeing, online: boolean, count: number): string {
  const oldest = ageing.state !== 'ok' && ageing.oldestAgeMs !== null
    ? ` · oldest ${queueAgeText(ageing.oldestAgeMs)}`
    : ''
  if (!online)   return `Offline · ${count} queued${oldest}`
  if (count > 0) return `${count} queued${oldest} — tap to view`
  return 'Synced'
}

function BadgeIcon({ ageing, online, count, justSent }: { ageing: QueueAgeing; online: boolean; count: number; justSent: boolean }) {
  if (ageing.state !== 'ok') return <Clock size={14} />
  if (!online)   return <CloudOff size={14} />
  if (count > 0) return <RefreshCw size={14} />
  return justSent ? <Check size={14} /> : null
}

export default function OfflineQueueBadge() {
  const [breakdown, setBreakdown] = useState<QueueBreakdown>(EMPTY)
  const [online,    setOnline]    = useState(typeof navigator !== 'undefined' ? navigator.onLine : true)
  const [flushing,  setFlushing]  = useState(false)
  const [justSent,  setJustSent]  = useState(false)
  const [open,      setOpen]      = useState(false)
  const count = breakdown.total
  const ageing = useQueueAgeing(count)

  // Track online/offline at the navigator level.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onOnline  = () => setOnline(true)
    const onOffline = () => setOnline(false)
    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // Refresh breakdown whenever the queue changes (writes happen, drain runs).
  useEffect(() => {
    const refresh = () => { void pendingBreakdown().then(setBreakdown) }
    refresh()
    return onQueueChanged(refresh)
  }, [])

  // Brief "✓ synced" flash after the count drops to zero.
  useEffect(() => {
    if (count === 0 && flushing) {
      setJustSent(true)
      const id = setTimeout(() => setJustSent(false), 2000)
      return () => clearTimeout(id)
    }
  }, [count, flushing])

  // Auto-close the modal when there's nothing left to show — keeps the
  // operator from sitting on an empty list after a successful drain.
  useEffect(() => {
    if (open && count === 0 && online) {
      const id = setTimeout(() => setOpen(false), 1200)
      return () => clearTimeout(id)
    }
  }, [open, count, online])

  // Render only when there's something to show.
  if (count === 0 && online && !justSent) return null

  // fallow-ignore-next-line complexity
  const breakdownLabel = (() => {
    const parts: string[] = []
    if (breakdown.tipping  > 0) parts.push(`${breakdown.tipping} tip`)
    if (breakdown.cooling_batch > 0) parts.push(`${breakdown.cooling_batch} dolly`)
    // cooling_transition / batch_tip / span_clock were in QueueBreakdown and in
    // KIND_LABEL from the day they shipped — only this summary line was missed,
    // so a backlog of PAY-BEARING team clock-ins read as an anonymous "3 queued"
    // naming none of them. A foreman triaging the badge could not tell payroll
    // from a scouting note. Same OP-5 gap already closed for pick_tallies.
    if (breakdown.cooling_transition > 0) parts.push(`${breakdown.cooling_transition} cool`)
    if (breakdown.batch_tip > 0) parts.push(`${breakdown.batch_tip} batch tip`)
    if (breakdown.span_clock > 0) parts.push(`${breakdown.span_clock} clock`)
    // P45-10: the identical one-liner this comment block memorialises for
    // span_clock — harvest_load (MONEY kind) was in the breakdown and the
    // label map but absent from this summary, so queued tractor loads read
    // as an anonymous "N queued".
    if (breakdown.harvest_load > 0) parts.push(`${breakdown.harvest_load} load`)
    // Third time this line has needed the same addition — a kind in the
    // breakdown and the label map but not HERE reads as an anonymous
    // "N queued". harvest_general is a MONEY kind: it carries clock scans.
    if (breakdown.harvest_general > 0) parts.push(`${breakdown.harvest_general} gen. work`)
    // Fourth time. Not a money kind, but an anonymous "N queued" is exactly as
    // unreadable for a lend as it was for a load.
    if (breakdown.harvest_team_move > 0) parts.push(`${breakdown.harvest_team_move} team move`)
    if (breakdown.farm_day_lend > 0) parts.push(`${breakdown.farm_day_lend} crew lend`)
    if (breakdown.piece_bag_issue > 0) parts.push(`${breakdown.piece_bag_issue} bag issue`)
    if (breakdown.ppe_issue > 0) parts.push(`${breakdown.ppe_issue} PPE`)
    if (breakdown.pallet   > 0) parts.push(`${breakdown.pallet} pallet`)
    if (breakdown.scouting > 0) parts.push(`${breakdown.scouting} obs`)
    if (breakdown.scout_walk > 0) parts.push(`${breakdown.scout_walk} walk`)
    if (breakdown.trap_visit > 0) parts.push(`${breakdown.trap_visit} trap visit`)
    if (breakdown.security_incident > 0) parts.push(`${breakdown.security_incident} incident`)
    if (breakdown.pick     > 0) parts.push(`${breakdown.pick} pick`)
    if (breakdown.pick_tallies > 0) parts.push(`${breakdown.pick_tallies} drops`)
    if (breakdown.packout_drops > 0) parts.push(`${breakdown.packout_drops} packing scans`)
    if (breakdown.spray    > 0) parts.push(`${breakdown.spray} spray`)
    if (breakdown.spray_complete > 0) parts.push(`${breakdown.spray_complete} spray✓`)
    if (breakdown.reject   > 0) parts.push(`${breakdown.reject} reject`)
    if (breakdown.phenology > 0) parts.push(`${breakdown.phenology} phen`)
    if (breakdown.fert     > 0) parts.push(`${breakdown.fert} fert`)
    if (breakdown.fert_complete > 0) parts.push(`${breakdown.fert_complete} fert✓`)
    if (breakdown.irrigation > 0) parts.push(`${breakdown.irrigation} irrigation`)
    if (breakdown.drain_reading > 0) parts.push(`${breakdown.drain_reading} drain`)
    if (breakdown.qc       > 0) parts.push(`${breakdown.qc} QC`)
    if (breakdown.delivery > 0) parts.push(`${breakdown.delivery} delivery`)
    if (breakdown.fuel     > 0) parts.push(`${breakdown.fuel} fuel`)
    if (breakdown.equipment_service > 0) parts.push(`${breakdown.equipment_service} svc`)
    if (breakdown.vehicle_trip > 0) parts.push(`${breakdown.vehicle_trip} trip`)
    if (breakdown.field_operation > 0) parts.push(`${breakdown.field_operation} field op`)
    if (breakdown.labour_attest > 0) parts.push(`${breakdown.labour_attest} attest`)
    if (breakdown.labour_adjust > 0) parts.push(`${breakdown.labour_adjust} adjust`)
    if (breakdown.stock_movement > 0) parts.push(`${breakdown.stock_movement} stock`)
    // Fifth time. STK-32's counted figures are a whole cycle count when they
    // back up — never let them read as an anonymous "N queued".
    if (breakdown.stock_count_line > 0) parts.push(`${breakdown.stock_count_line} counted`)
    // Council T3.8 — flag failed entries that need manual attention.
    if (breakdown.failed   > 0) parts.push(`⚠ ${breakdown.failed} failed`)
    return parts.length === 0 ? '' : parts.join(' · ')
  })()


  return (
    <>
      {/* has-capture-fab: a page-scoped CaptureFab owns the low right-4 slot —
          vacate upward. Compound body selector so it deterministically beats
          the single-class variants at equal utility. */}
      <div className="fixed bottom-20 [body.has-bottom-nav_&]:bottom-24 [body.update-banner-visible_&]:bottom-40 [body.has-capture-fab.has-bottom-nav_&]:bottom-[8.5rem] transition-[bottom] duration-200 right-4 z-chrome pointer-events-auto print:hidden">
        <button
          onClick={() => setOpen(true)}
          title={breakdownLabel ? `${breakdownLabel} pending — tap for detail` : 'Tap for detail'}
          className={clsx(
            'rounded-full shadow-lg px-4 py-2.5 text-sm coarse:min-h-[44px] sm:px-3 sm:py-2 sm:text-xs font-semibold flex items-center gap-1.5 transition-colors',
            badgeFill(ageing, online, count),
          )}>
          <BadgeIcon ageing={ageing} online={online} count={count} justSent={justSent} />
          <span>{badgeText(ageing, online, count)}</span>
        </button>
      </div>

      {open && (
        <QueueDetailModal
          flushing={flushing}
          online={online}
          onClose={() => setOpen(false)}
          onSync={async () => {
            if (flushing) return
            setFlushing(true)
            clearDrainBackoff()   // explicit "Sync now" bypasses any active W6 backoff window
            try { await drainQueue() }
            finally { setFlushing(false) }
          }}
        />
      )}
    </>
  )
}

// Shown above everything else: work held only on this phone for too long.
function AgeingBanner({ ageing }: { ageing: QueueAgeing }) {
  if (ageing.state === 'ok' || ageing.oldestAgeMs === null) return null
  const age = queueAgeText(ageing.oldestAgeMs)
  const critical = ageing.state === 'critical'
  return (
    <div
      role={critical ? 'alert' : 'status'}
      className={clsx(
        'mb-2 rounded-lg px-3 py-2 flex items-start gap-2 border',
        critical ? 'bg-danger-soft border-danger-edge text-danger' : 'bg-warning-soft border-warning-edge text-warning',
      )}>
      {critical
        ? <AlertCircle size={13} className="shrink-0 mt-px" />
        : <Clock size={13} className="shrink-0 mt-px" />}
      <p className="text-xs">
        {critical
          ? `The oldest save has waited ${age}. If this phone is lost, this work is lost. Pay work that arrives after the pay run closes can be refused. Get signal now.`
          : `The oldest save has waited ${age}. This work is only on this phone. Get signal soon so it reaches the office.`}
      </p>
    </div>
  )
}

// "Sync now" deliberately SKIPS entries the drainer gave up on, so a cause
// fixed server-side (a corrected role, a re-pointed badge) leaves them sitting
// there looking like lost work until someone taps each one individually — which
// is how a picker's pay stayed invisible for six days after the bug behind it
// was fixed (Anchor 2026-07-27). Say that plainly, and offer the one tap that
// re-arms the lot. Below 2 the per-entry Retry is right there, so this would
// just be noise.
function FailedRetryBanner({ count }: { count: number }) {
  if (count < 2) return null
  return (
    <div className="mb-2 rounded-lg bg-emerald-950/30 border border-emerald-900/40 px-3 py-2 flex items-center justify-between gap-3">
      <p className="text-xs text-emerald-200">
        {count} entries have stopped retrying. Sync won’t touch them — if the cause is fixed, re-arm them all.
      </p>
      <button
        type="button"
        onClick={() => { void retryAllFailed() }}
        className="shrink-0 inline-flex items-center justify-center gap-1 px-3 py-2.5 coarse:min-h-[44px] text-xs sm:px-2 sm:py-1 sm:text-[11px] rounded-md text-emerald-300 hover:text-emerald-200 hover:bg-emerald-950/60 border border-emerald-900/60">
        <RotateCw size={11} /> Retry all
      </button>
    </div>
  )
}

// Never promise "tap sync to send now" while some entries CAN'T be sent that
// way — that sentence is what makes a stopped entry read as a stuck one.
function footerHint(failedCount: number, online: boolean): string {
  if (failedCount > 0) return 'Sync sends the waiting entries — stopped ones need Retry.'
  return online
    ? 'Tap sync to send now.'
    : 'Will send when back online — or tap sync to try anyway.'
}

// ---------------------------------------------------------------------------
// Detail modal — shows each queued entry with kind + queued_at + attempts
// + last error. Re-fetches whenever the queue notifies a change so the
// list updates live during a drain.
// ---------------------------------------------------------------------------
function QueueDetailModal({
  flushing, online, onSync, onClose,
}: {
  flushing: boolean
  online:   boolean
  onSync:   () => void | Promise<void>
  onClose:  () => void
}) {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const failedCount = entries.filter(e => e.failed).length
  const ageing = queueAgeing(entries, Date.now())

  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      const list = await listPending()
      if (cancelled) return
      // Oldest first — operators usually want to know what's been waiting
      // longest, not the most recent capture.
      list.sort((a, b) => a.queued_at.localeCompare(b.queued_at))
      setEntries(list)
      setLoading(false)
    }
    void refresh()
    const off = onQueueChanged(refresh)
    return () => { cancelled = true; off() }
  }, [])

  return (
    <Modal
      onClose={onClose}
      size="md"
      title={
        <span className="flex items-center gap-2">
          {online ? <RefreshCw size={16} className="text-blue-400 shrink-0" /> : <CloudOff size={16} className="text-amber-400 shrink-0" />}
          <span className="truncate">Pending offline queue</span>
        </span>
      }
      footer={() => (
        <footer className="px-4 py-3 border-t hairline flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-gray-500">
            {footerHint(failedCount, online)}
          </p>
          {/* Sync now is deliberately NOT gated on navigator.onLine (idle-wake
              audit 6.2): on VPN/virtual-NIC devices it lies false chronically,
              which made the operator's own tap a silent no-op — exactly why
              they escalated to a full reload. The drain itself decides (it
              no-ops fast when genuinely offline, and trusts the reconciler's
              link probe over the flag). */}
          <button
            onClick={onSync}
            disabled={flushing || entries.length === 0}
            className={clsx(
              'rounded-xl px-4 py-2.5 coarse:min-h-[44px] sm:px-3 sm:py-2 text-sm font-semibold flex items-center gap-1.5 transition-colors',
              flushing || entries.length === 0
                ? 'surface text-gray-500 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-600 text-white'
            )}>
            <RefreshCw size={14} className={flushing ? 'animate-spin' : ''} />
            {flushing ? 'Syncing…' : 'Sync now'}
          </button>
        </footer>
      )}>
        <AgeingBanner ageing={ageing} />
        <FailedRetryBanner count={failedCount} />

        {IS_IOS && entries.length > 0 && (
          <div className="mb-2 rounded-lg bg-amber-950/40 border border-amber-900/40 px-3 py-2 flex items-start gap-2">
            <CloudOff size={13} className="text-amber-400 shrink-0 mt-px" />
            <p className="text-xs text-amber-200">
              Keep this app open until everything syncs — on iPhone/iPad it can’t sync in the background.
            </p>
          </div>
        )}

        <div>
          {loading ? (
            <div className="py-10 flex justify-center">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              <Check size={28} className="mx-auto mb-2 text-emerald-400" />
              All caught up.
            </div>
          ) : (
            <ul className="divide-y divide-line-subtle">
              {/* Pre-existing row renderer; this PR only reworded the footer,
                  but the new-only gate flags any edit near an over-threshold
                  function. Suppress.
                  fallow-ignore-next-line complexity */}
              {entries.map(e => (
                <li key={e.id} className="py-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                      {KIND_LABEL[e.kind]}
                      {e.failed && (
                        <span className="text-[9px] uppercase tracking-wider rounded px-1.5 py-0.5 bg-red-900/60 text-red-300">
                          failed
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap">
                      {formatRelative(e.queued_at)}
                    </p>
                  </div>
                  {e.kind === 'ppe_issue' && (
                    <p className="text-xs text-fg-secondary mt-0.5 break-words">{e.item_label} · {e.worker_label}</p>
                  )}
                  <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
                    {e.attempt_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock size={11} /> {e.attempt_count} attempt{e.attempt_count === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  {e.last_error && (
                    <p className="text-xs text-amber-300 mt-1 flex items-start gap-1">
                      <AlertCircle size={11} className="shrink-0 mt-px" />
                      <span className="break-words">{e.last_error}</span>
                    </p>
                  )}
                  {/* Failed entries (drainer gave up) get a Retry — re-arms the
                      entry for the next drain, for when the cause was fixable
                      after the fact (a corrected role, a freed unique value, a
                      long outage past the retry cap). Clear stays for genuinely
                      broken payloads; both are confirm-gated so a stray tap
                      doesn't drop work. */}
                  <div className="mt-1.5 flex justify-end gap-3 sm:gap-2">
                    {e.failed && (
                      <button
                        type="button"
                        onClick={() => { void retryQueueEntry(e.id) }}
                        className="inline-flex items-center justify-center gap-1 px-3 py-2.5 coarse:min-h-[44px] text-xs sm:px-2 sm:py-1 sm:text-[11px] rounded-md text-emerald-300 hover:text-emerald-200 hover:bg-emerald-950/40 border border-emerald-900/40">
                        <RotateCw size={11} /> Retry
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        const label = KIND_LABEL[e.kind]
                        const ok = await confirmDialog({
                          title:   'Clear this entry?',
                          message: `Discard this ${label.toLowerCase()} from the offline queue?\n\n`
                                 + 'It will be permanently removed. If it represents real work, '
                                 + 're-enter it through the normal form after clearing.'
                                 + (e.kind === 'ppe_issue' ? '\n\nThis deletes both signatures. The worker still holds the item.' : ''),
                          confirm: 'Clear',
                          tone:    'danger',
                        })
                        if (ok) await discardQueueEntry(e.id)
                      }}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2.5 coarse:min-h-[44px] text-xs sm:px-2 sm:py-1 sm:text-[11px] rounded-md text-red-300 hover:text-red-200 hover:bg-red-950/40 border border-red-900/40">
                      <Trash2 size={11} /> Clear
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
    </Modal>
  )
}

// "2 min ago" / "1 h ago" / "Tuesday 14:32" — short helper local to this
// component because the queue modal is the only consumer of a sub-day
// relative-time format.
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  const now  = Date.now()
  const diffSec = Math.max(0, Math.round((now - then) / 1000))
  if (diffSec < 60)        return `${diffSec}s ago`
  if (diffSec < 3600)      return `${Math.floor(diffSec / 60)} min ago`
  if (diffSec < 86_400)    return `${Math.floor(diffSec / 3600)} h ago`
  return new Date(iso).toLocaleString('en-ZA', {
    weekday: 'short', hour: '2-digit', minute: '2-digit',
  })
}
